/**
 * Basket finance block: "Pay now OR Payments from £X / mo" above checkout in
 * the basket drawer and basket page, and the View details popup listing every
 * Klarna and Snap plan for the basket total.
 *
 * Snap maths matches the Snap payment plans block at checkout: the APR is the
 * effective annual rate, monthly rate = (1 + APR)^(1/12) - 1.
 */
(function () {
  const configEl = document.querySelector('[data-cart-finance-config]');
  const dialog = document.querySelector('[data-cart-finance-dialog]');
  if (!configEl || !dialog) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (error) {
    return;
  }

  const KLARNA_LOGO = 'https://cdn.shopify.com/s/files/1/0642/8151/0127/files/Klarna.svg?v=1785379483';
  const SNAP_LOGO = (dialog.querySelector('.cart-finance-dialog__partner-logo--snap') || {}).src || '';

  const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
  const formatPence = (pence) => money.format(pence / 100);

  const apr = parseFloat(config.snapApr) || 0;
  const monthlyRate = Math.pow(1 + apr / 100, 1 / 12) - 1;
  const snapTerms = String(config.snapTerms || '')
    .split(',')
    .map((term) => parseInt(term, 10))
    .filter((term) => term > 0)
    .sort((a, b) => a - b);
  const snapFreeMonths = parseInt(config.snapFreeMonths, 10) || 0;

  const inRange = (total, min, max) =>
    total >= (Number(min) || 0) && (max === '' || max == null || total <= Number(max));

  /**
   * @param {number} total - Basket total in pence
   * @returns {{ free: object[], longer: object[] }}
   */
  function getPlans(total) {
    const free = [];
    const longer = [];
    if (!(total > 0)) return { free, longer };

    if (inRange(total, config.klarnaMin, config.klarnaMax)) {
      free.push({ provider: 'klarna', months: 3, payment: Math.round(total / 3), apr: 0 });
    }

    if (config.snapEnabled && inRange(total, config.snapMin, config.snapMax)) {
      if (snapFreeMonths) {
        free.push({ provider: 'snap', months: snapFreeMonths, payment: Math.round(total / snapFreeMonths), apr: 0 });
      }
      if (monthlyRate > 0) {
        snapTerms.forEach((months) => {
          const payment = Math.round((total * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months)));
          longer.push({ provider: 'snap', months, payment, apr });
        });
      }
    }

    return { free, longer };
  }

  /** Lowest monthly payment across every plan, or 0 when none apply. */
  function getFromPayment(total) {
    const { free, longer } = getPlans(total);
    const payments = free.concat(longer).map((plan) => plan.payment);
    return payments.length ? Math.min(...payments) : 0;
  }

  /** @param {HTMLElement} block */
  function renderBlock(block) {
    const total = Number(block.dataset.total);
    const from = getFromPayment(total);
    block.querySelectorAll('[data-finance-later]').forEach((el) => {
      el.hidden = !from;
    });
    block.toggleAttribute('data-single', !from);
    const fromEl = block.querySelector('[data-finance-from]');
    if (fromEl && from) fromEl.textContent = formatPence(from);
  }

  /**
   * Re-run with a new basket total (called by assets/main.js after cart changes).
   * @param {number} [total] - Pence. Omit to re-render from each block's data-total.
   */
  function update(total) {
    document.querySelectorAll('[data-cart-finance]').forEach((block) => {
      if (total != null) block.dataset.total = String(total);
      renderBlock(block);
    });
  }

  function planRow(plan) {
    const logo = plan.provider === 'klarna' ? KLARNA_LOGO : SNAP_LOGO;
    const name = plan.provider === 'klarna' ? 'Klarna' : 'Snap Finance';
    const aprText = plan.apr ? `${plan.apr}% APR` : '0% APR';
    const li = document.createElement('li');
    li.className = 'cart-finance-dialog__row';
    li.innerHTML = `
      <span class="cart-finance-dialog__row-term">
        <img class="cart-finance-dialog__row-logo" src="${logo}" alt="${name}" width="40" height="18">
        <span><strong>${plan.months}</strong> monthly instalments of</span>
      </span>
      <span class="cart-finance-dialog__row-price">
        <strong>${formatPence(plan.payment)}</strong><span class="cart-finance-dialog__row-apr">${aprText}</span>
      </span>`;
    return li;
  }

  /** Representative example for the longest Snap plan with interest. */
  function exampleText(total, plan) {
    const totalPayable = plan.payment * plan.months;
    const annualRate = (monthlyRate * 12 * 100).toFixed(2);
    return (
      `Representative example: cash price ${formatPence(total)}. Amount of credit ${formatPence(total)}. ` +
      `${plan.months} monthly repayments of ${formatPence(plan.payment)}. Representative APR ${plan.apr}%. ` +
      `Annual rate of interest ${annualRate}% fixed. Total amount repayable ${formatPence(totalPayable)} ` +
      `including interest of ${formatPence(totalPayable - total)}.`
    );
  }

  /**
   * @param {number} total - Basket total in pence
   * @param {HTMLElement} trigger - The View details button
   */
  function openDialog(total, trigger) {
    const { free, longer } = getPlans(total);

    dialog.querySelector('[data-finance-dialog-total]').textContent = formatPence(total);

    [['free', free], ['longer', longer]].forEach(([key, plans]) => {
      const group = dialog.querySelector(`[data-finance-group="${key}"]`);
      const rows = dialog.querySelector(`[data-finance-rows="${key}"]`);
      rows.replaceChildren(...plans.map(planRow));
      group.hidden = !plans.length;
    });

    const example = dialog.querySelector('[data-finance-example]');
    const longest = longer[longer.length - 1];
    example.textContent = longest ? exampleText(total, longest) : '';

    const providers = new Set(free.concat(longer).map((plan) => plan.provider));
    dialog.querySelectorAll('[data-finance-partner]').forEach((el) => {
      el.hidden = !providers.has(el.dataset.financePartner);
    });

    // Open from inside the basket drawer popover when there is one, so the
    // modal stacks on top of the drawer instead of closing it.
    const host = trigger.closest('[popover]') || document.body;
    if (dialog.parentElement !== host) host.appendChild(dialog);

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-finance-details]');
    if (trigger) {
      event.preventDefault();
      const block = trigger.closest('[data-cart-finance]');
      openDialog(Number(block && block.dataset.total), trigger);
      return;
    }
    if (event.target.closest('[data-cart-finance-close]') || event.target === dialog) {
      dialog.close();
    }
  });

  // The basket drawer repaints its summary from section HTML, so render any
  // finance block that arrives in new markup.
  new MutationObserver((mutations) => {
    const added = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) => node.nodeType === 1 && (node.matches('[data-cart-finance]') || node.querySelector('[data-cart-finance]'))
      )
    );
    if (added) update();
  }).observe(document.body, { childList: true, subtree: true });

  window.theme = window.theme || {};
  window.theme.cartFinance = { update };

  update();
})();
